import { bigintToFixed } from "rujira.js";
import {
  assetLabel,
  compareNullableBigInts,
  compareNullableNumbers,
  compareStrings,
  normalizeBigIntDecimals,
  SortDir,
} from "rujira.ui";
import type { RecurringOrdersFragment$data } from "./__generated__/RecurringOrdersFragment.graphql";
import { Side } from "../../types";
export type RecurringOrder = RecurringOrdersFragment$data[number];

type NodesArray = NonNullable<NonNullable<RecurringOrder["config"]>["nodes"]>;

type AnyNode = NonNullable<NodesArray[number]>;

type CalcActionNode = Extract<AnyNode, { __typename: "CalcAction" }>;
type ActionUnion = NonNullable<CalcActionNode["action"]>;
type SwapAction = Extract<ActionUnion, { __typename: "CalcActionSwap" }>;
export type SwapActionNode = CalcActionNode & { action: SwapAction };
type DistributeAction = Extract<
  ActionUnion,
  { __typename: "CalcActionDistribute" }
>;
export type DistributeActionNode = CalcActionNode & {
  action: DistributeAction;
};

type CalcConditionNode = Extract<AnyNode, { __typename: "CalcCondition" }>;
type ConditionUnion = NonNullable<CalcConditionNode["condition"]>;
type ScheduleCondition = Extract<
  ConditionUnion,
  { __typename: "CalcConditionSchedule" }
>;
export type ScheduleConditionNode = CalcConditionNode & {
  condition: ScheduleCondition;
};

const SORT_KEYS = [
  "pair",
  "status",
  "progress",
  "timeRemaining",
  "every",
  "nextExecution",
  "swapAmount",
  "priceImpact",
  "priceLimit",
  "swapMultiplier",
  "remaining",
  "unclaimed",
] as const;

export type SortKey = (typeof SORT_KEYS)[number];

const getRecurringOrderConfig = (order: RecurringOrder) => {
  const finNode = order.config.nodes
    ?.find(
      (node): node is SwapActionNode =>
        node.__typename === "CalcAction" &&
        node.action.__typename === "CalcActionSwap"
    )
    ?.action.routes.find((route) => route.__typename === "CalcSwapRouteFin");
  const pair = finNode?.pair;

  if (
    !order.config ||
    !order.config.nodes ||
    ![5, 7].includes(order.config.nodes.length)
  ) {
    return null;
  }

  const hasBalanceNode = order.config.nodes[0];
  if (
    hasBalanceNode.__typename !== "CalcCondition" ||
    hasBalanceNode.condition.__typename !== "CalcConditionBalanceAvailable"
  ) {
    return null;
  }

  const skipScheduleNode = order.config.nodes[2];
  if (
    skipScheduleNode.__typename !== "CalcCondition" ||
    skipScheduleNode.condition.__typename !== "CalcConditionSchedule" ||
    (skipScheduleNode.condition.cadence.__typename !== "CalcCadenceTime" &&
      skipScheduleNode.condition.cadence.__typename !== "CalcCadenceBlocks")
  ) {
    return null;
  }

  const swapScheduleNode = order.config.nodes[3];
  if (
    swapScheduleNode.__typename !== "CalcCondition" ||
    swapScheduleNode.condition.__typename !== "CalcConditionSchedule" ||
    swapScheduleNode.condition.cadence.__typename !==
      skipScheduleNode.condition.cadence.__typename
  ) {
    return null;
  }

  const swapNode = order.config.nodes[4];
  if (
    swapNode.__typename !== "CalcAction" ||
    swapNode.action.__typename !== "CalcActionSwap"
  ) {
    return null;
  }

  const distributeNode = order.config.nodes[6];
  if (
    distributeNode &&
    (distributeNode.__typename !== "CalcAction" ||
      distributeNode.action.__typename !== "CalcActionDistribute")
  ) {
    return null;
  }

  if (!pair) return null;

  const swapAmount = swapNode.action.swapAmount;
  const minimumReceiveAmount = swapNode.action.minimumReceiveAmount;
  const swapAdjustment = swapNode.action.adjustment;
  const side =
    swapAmount.asset.metadata.symbol === pair.assetQuote?.metadata.symbol
      ? Side.Quote
      : Side.Base;
  const remainingSwapBalance = BigInt(
    order.balances?.find(
      (balance) =>
        balance.asset.metadata.symbol === swapAmount.asset.metadata.symbol
    )?.amount || "0"
  );
  const acquiredReceiveAmount = BigInt(
    order.balances?.find(
      (balance) =>
        balance.asset.metadata.symbol ===
        minimumReceiveAmount.asset.metadata.symbol
    )?.amount || "0"
  );
  // Preserve nearest-swap estimates without losing precision in the balance division.
  const swapUnits = BigInt(swapAmount.amount);
  const remainingSwaps =
    swapUnits > 0n
      ? Number((remainingSwapBalance * 2n + swapUnits) / (swapUnits * 2n))
      : 0;
  const previousSwap =
    swapScheduleNode.condition.cadence.__typename === "CalcCadenceBlocks"
      ? swapScheduleNode.condition.cadence.previousBlock
      : swapScheduleNode.condition.cadence.previousTime;
  const previousSkip =
    skipScheduleNode.condition.cadence.__typename === "CalcCadenceBlocks"
      ? skipScheduleNode.condition.cadence.previousBlock
      : skipScheduleNode.condition.cadence.previousTime;
  const isSkipping = (previousSkip || 0) > (previousSwap || 0);

  return {
    skipScheduleNode: skipScheduleNode as ScheduleConditionNode,
    swapScheduleNode: swapScheduleNode as ScheduleConditionNode,
    swapNode: swapNode as SwapActionNode,
    distributeNode: distributeNode as DistributeActionNode | undefined,
    autoDistribute: !!distributeNode,
    swapAmount,
    minimumReceiveAmount,
    swapAdjustment,
    remainingSwapBalance,
    acquiredReceiveAmount,
    remainingSwaps,
    maximumSlippageBps: swapNode.action.maximumSlippageBps,
    routes: swapNode.action.routes,
    pair,
    side,
    cadence: isSkipping
      ? skipScheduleNode.condition.cadence
      : swapScheduleNode.condition.cadence,
    isSkipping,
    swaps: swapScheduleNode.condition.executions || 0,
  };
};

export type RecurringOrderConfig = NonNullable<
  ReturnType<typeof getRecurringOrderConfig>
>;

export type PreparedRecurringOrder = {
  order: RecurringOrder;
  config: RecurringOrderConfig | null;
  pairLabel: string;
  status: string;
  progress: number | null;
  remainingDuration: number | null;
  cadenceDuration: number | null;
  secondsUntilNextExecution: number | null;
  priceThreshold: bigint | null;
  swapAmount: bigint | null;
  priceImpact: number | null;
  swapMultiplier: bigint | null;
  remaining: bigint | null;
  unclaimed: bigint | null;
  swapAdjustmentDescription: string | null;
};

export const prepareRecurringOrder = (
  order: RecurringOrder,
  blockHeight: number | undefined,
  now: number
): PreparedRecurringOrder => {
  const config = getRecurringOrderConfig(order);
  if (!config) {
    return {
      order,
      config: null,
      pairLabel: order.label,
      status: order.balances?.some((balance) => BigInt(balance.amount) > 10n)
        ? order.status.toLowerCase()
        : "completed",
      progress: null,
      remainingDuration: null,
      cadenceDuration: null,
      secondsUntilNextExecution: null,
      priceThreshold: null,
      swapAmount: null,
      priceImpact: null,
      swapMultiplier: null,
      remaining: null,
      unclaimed: null,
      swapAdjustmentDescription: null,
    };
  }

  const {
    cadence,
    minimumReceiveAmount,
    remainingSwapBalance,
    remainingSwaps,
    side,
    swapAdjustment,
    swapAmount,
    swaps,
    acquiredReceiveAmount,
    maximumSlippageBps,
  } = config;
  const swap = BigInt(swapAmount.amount);
  const receive = BigInt(minimumReceiveAmount.amount);
  const priceThreshold =
    receive === 0n || swap === 0n
      ? 0n
      : side === Side.Quote
        ? (swap * 10n ** 12n) / receive
        : (receive * 10n ** 12n) / swap;
  const secondsUntilNextExecution = recurringSecondsUntilNextExecution(
    config,
    blockHeight,
    now
  );
  const cadenceDuration =
    cadence.__typename === "CalcCadenceBlocks"
      ? cadence.interval * 6
      : cadence.duration;
  const remainingDuration = cadenceDuration * remainingSwaps;
  const progress =
    remainingSwaps === 0
      ? 100
      : remainingSwaps && swaps
        ? Math.min(99, Math.round((swaps / (swaps + remainingSwaps)) * 100))
        : 0;
  const swapMultiplier =
    swapAdjustment.__typename === "CalcSwapAmountAdjustmentLinearScalar"
      ? BigInt(swapAdjustment.scalar)
      : 10n ** 12n;
  const basePrice =
    Number(swapAmount.amount) /
    Number(
      swapAdjustment.__typename === "CalcSwapAmountAdjustmentLinearScalar"
        ? swapAdjustment.baseReceiveAmount.amount
        : swapAmount.amount
    );
  const directionalPrice = side === Side.Quote ? basePrice : 1 / basePrice;
  const swapAdjustmentDescription =
    swapAdjustment.__typename === "CalcSwapAmountAdjustmentFixed"
      ? "1.0x"
      : `${bigintToFixed(swapMultiplier).replace(/\.?0+$/, "")}x from ${directionalPrice.toLocaleString(
          undefined,
          { minimumFractionDigits: 2 }
        )}`;

  return {
    order,
    config,
    pairLabel: `${assetLabel(swapAmount.asset)}/${assetLabel(minimumReceiveAmount.asset)}`,
    status: remainingSwaps > 0 ? order.status.toLowerCase() : "completed",
    progress,
    remainingDuration,
    cadenceDuration,
    secondsUntilNextExecution,
    priceThreshold,
    swapAmount: normalizeBigIntDecimals(
      swapAmount.amount,
      swapAmount.asset.metadata.decimals
    ),
    priceImpact: maximumSlippageBps,
    swapMultiplier,
    remaining: normalizeBigIntDecimals(
      remainingSwapBalance,
      swapAmount.asset.metadata.decimals
    ),
    unclaimed: normalizeBigIntDecimals(
      acquiredReceiveAmount,
      minimumReceiveAmount.asset.metadata.decimals
    ),
    swapAdjustmentDescription,
  };
};

export const comparePreparedOrders = (
  a: PreparedRecurringOrder,
  b: PreparedRecurringOrder,
  sortBy: SortKey,
  sortDir: SortDir
) => {
  switch (sortBy) {
    case "pair":
      return compareStrings(a.pairLabel, b.pairLabel, sortDir);
    case "status":
      return compareStrings(a.status, b.status, sortDir);
    case "progress":
      return compareNullableNumbers(a.progress, b.progress, sortDir);
    case "timeRemaining":
      return compareNullableNumbers(
        a.remainingDuration,
        b.remainingDuration,
        sortDir
      );
    case "every":
      return compareNullableNumbers(
        a.cadenceDuration,
        b.cadenceDuration,
        sortDir
      );
    case "nextExecution":
      return compareNullableNumbers(
        a.secondsUntilNextExecution,
        b.secondsUntilNextExecution,
        sortDir
      );
    case "swapAmount":
      return compareNullableBigInts(a.swapAmount, b.swapAmount, sortDir);
    case "priceImpact":
      return compareNullableNumbers(a.priceImpact, b.priceImpact, sortDir);
    case "priceLimit":
      return compareNullableBigInts(
        a.priceThreshold,
        b.priceThreshold,
        sortDir
      );
    case "swapMultiplier":
      return compareNullableBigInts(
        a.swapMultiplier,
        b.swapMultiplier,
        sortDir
      );
    case "remaining":
      return compareNullableBigInts(a.remaining, b.remaining, sortDir);
    case "unclaimed":
      return compareNullableBigInts(a.unclaimed, b.unclaimed, sortDir);
  }
};

const recurringSecondsUntilNextExecution = (
  { cadence }: RecurringOrderConfig,
  blockHeight: number | undefined,
  now: number
) => {
  return "interval" in cadence && cadence.previousBlock && blockHeight
    ? (cadence.interval - (blockHeight - cadence.previousBlock)) * 6
    : "duration" in cadence && cadence.previousTime
      ? Math.floor(
          (Date.parse(cadence.previousTime) + cadence.duration * 1000 - now) /
            1000
        )
      : 0;
};

// A block update only refreshes timing; config, balances and status stay prepared.
export const updateRecurringOrderTiming = (
  row: PreparedRecurringOrder,
  blockHeight: number | undefined,
  now: number
): PreparedRecurringOrder =>
  row.config
    ? {
        ...row,
        secondsUntilNextExecution: recurringSecondsUntilNextExecution(
          row.config,
          blockHeight,
          now
        ),
      }
    : row;
